import React, { useState } from 'react'
import { Row, Col, Pagination, Modal, Tabs, Tab } from 'react-bootstrap'
import { Sliders, BarChart2 } from 'lucide-react'
import styled from 'styled-components'
import MiniClassDistributionCard from '../../../components/MiniClassDistributionCard'

interface ModelDatasetViewHeaderProps {
  imagesPerRow: number
  onImagesPerRowChange?: (size: number) => void
  currentPage: number
  totalImages: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  activeTab: 'training' | 'validation'
  onTabChange: (tab: 'training' | 'validation') => void
  datasetId: string
}

const SizeControl = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem;
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 12px;
  justify-self: end;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    color: #fff;
    font-size: 0.9rem;
    font-weight: 500;
  }
`

const RangeInput = styled.input`
  width: 150px;
`

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 12px;
  justify-self: center;
  z-index: 1;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  .pagination {
    margin: 0;
    position: relative;
    z-index: 1;
  }

  .page-link {
    background-color: transparent;
    border-color: rgba(102, 126, 234, 0.3);
    color: #fff;
    padding: 0.35rem 0.6rem;
    transition: all 0.3s ease;
    font-size: 0.85rem;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    position: relative;
    z-index: 1;
    border-radius: 8px;
    margin: 0 2px;

    &:hover {
      background-color: rgba(102, 126, 234, 0.2);
      border-color: rgba(102, 126, 234, 0.5);
      transform: translateY(-1px);
    }
  }

  .page-item.active .page-link {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-color: #667eea;
    color: #fff;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  }

  .page-item.disabled .page-link {
    background-color: transparent;
    border-color: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.3);
  }
`

const PaginationInfo = styled.div`
  color: #fff;
  font-size: 0.85rem;
  white-space: nowrap;
  padding: 0.35rem 0.75rem;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  font-weight: 500;
`

const IconButton = styled.button`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  margin-right: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);

  &:hover {
    background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  svg {
    color: #fff;
  }
`

const StyledModal = styled(Modal)`
  .modal-content {
    background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
    border: 1px solid rgba(102, 126, 234, 0.2);
    border-radius: 16px;
    color: #fff;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
  }

  .modal-header {
    background: transparent;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px 16px 0 0;
    padding: 1rem;

    .modal-title {
      color: #fff;
      font-size: 1.1rem;
      font-weight: 600;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .btn-close {
      filter: invert(1) grayscale(100%) brightness(200%);
      opacity: 0.7;

      &:hover {
        opacity: 1;
      }
    }
  }

  .modal-body {
    background: transparent;
    border-radius: 0 0 16px 16px;
    padding: 1.5rem;
  }
`

const StyledTabs = styled(Tabs)`
  border-bottom: none;
  margin-bottom: 0;
  margin-right: 1rem;

  .nav-item {
    margin-right: 0.5rem;
  }

  .nav-link {
    background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
    border: 1px solid rgba(102, 126, 234, 0.2);
    color: rgba(255, 255, 255, 0.7);
    padding: 0.75rem 2rem;
    border-radius: 12px;
    transition: all 0.3s ease;
    font-size: 0.9rem;
    font-weight: 500;
    width: 180px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(10px);

    &:hover {
      background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
      color: #fff;
      border-color: rgba(102, 126, 234, 0.4);
      transform: translateY(-1px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    }

    &.active {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #fff;
      font-weight: 600;
      border-color: #667eea;
      box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
      transform: translateY(-1px);
    }
  }
`

const ModelDatasetViewHeader: React.FC<ModelDatasetViewHeaderProps> = ({
  imagesPerRow,
  onImagesPerRowChange,
  currentPage,
  totalImages,
  itemsPerPage,
  onPageChange,
  activeTab,
  onTabChange,
  datasetId,
}) => {
  const [showDistributionModal, setShowDistributionModal] = useState(false)
  const totalPages = Math.max(1, Math.ceil(totalImages / itemsPerPage))

  // Range is still based on the current page and totalImages (no extra filtering here),
  // but guard for the empty state so the label is consistent.
  const startItem = totalImages === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1
  const endItem = totalImages === 0 ? 0 : Math.min(currentPage * itemsPerPage, totalImages)

  return (
    <Row className="mb-4 align-items-center" style={{ width: '100%', padding: '0 1rem' }}>
      <Col xs={4} style={{ display: 'flex', alignItems: 'center' }}>
        <StyledTabs
          activeKey={activeTab}
          onSelect={(k) => onTabChange(k as 'training' | 'validation')}
          id="dataset-tabs"
        >
          <Tab eventKey="training" title="Training Data" />
          <Tab eventKey="validation" title="Validation Data" />
        </StyledTabs>
      </Col>
      <Col xs={4} style={{ display: 'flex', alignItems: 'center' }}>
        <PaginationContainer>
          <Pagination>
            <Pagination.First onClick={() => onPageChange(1)} disabled={currentPage === 1} />
            <Pagination.Prev
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            <Pagination.Item active>{currentPage}</Pagination.Item>
            <Pagination.Next
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
          <PaginationInfo>
            {startItem}-{endItem} of {totalImages} images
          </PaginationInfo>
        </PaginationContainer>
      </Col>
      <Col
        xs={4}
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          paddingRight: '0',
        }}
      >
        <IconButton
          onClick={() => setShowDistributionModal(true)}
          style={{ backgroundColor: '#800085', width: '4rem' }}
        >
          <BarChart2 color="#000" size={24} />
        </IconButton>
        {onImagesPerRowChange && (
          <SizeControl>
            <label>
              <Sliders size={16} />
              Size
            </label>
            <RangeInput
              type="range"
              min="1"
              max="6"
              value={7 - imagesPerRow}
              onChange={(e) =>
                onImagesPerRowChange && onImagesPerRowChange(7 - parseInt(e.target.value))
              }
              className="form-range"
            />
          </SizeControl>
        )}
      </Col>

      <StyledModal
        show={showDistributionModal}
        onHide={() => setShowDistributionModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Class Distribution</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <MiniClassDistributionCard showButtons={false} datasetId={datasetId} />
        </Modal.Body>
      </StyledModal>
    </Row>
  )
}

export default ModelDatasetViewHeader
